import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FirebaseContext from '../Firebase/context';
import { AuthUserContext } from '../Session';
import NotificationList from './NotificationList';
import './Notifications.css';

const PAGE_SIZE = 8;

const Notifications = ({
  lastCheckTime = 0,
  unreadMessages = 0,
  unreadLogs = 0,
  onClose,
  initialView = null,
  soundEnabled = false,
  toastsEnabled = false,
  onSoundEnabledChange,
  onToastsEnabledChange,
}) => {
  const { t } = useTranslation();
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);

  const [view, setView] = useState(
    () => initialView || localStorage.getItem('notificationActiveTab') || 'messages',
  );
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsCursor, setLogsCursor] = useState(null);

  const messagesListenerRef = useRef(null);
  const logsListenersRef = useRef([]);

  useEffect(() => {
    if (initialView) setView(initialView);
  }, [initialView]);

  useEffect(() => {
    if (!firebase) return;

    const listener = firebase.onMessagesFeed((items) => {
      const filtered = items.filter(msg =>
        !msg.organisation ||
        (authUser?.roles?.[msg.organisation] != null)
      );
      setMessages(filtered);
      setMessagesHasMore(filtered.length >= PAGE_SIZE);
      if (filtered.length > 0) {
        setMessagesCursor(filtered[filtered.length - 1].createdAt);
      }
      setMessagesLoading(false);
    }, PAGE_SIZE);

    messagesListenerRef.current = listener;

    return () => {
      if (messagesListenerRef.current) {
        firebase.offMessagesFeed(messagesListenerRef.current);
        messagesListenerRef.current = null;
      }
    };
  }, [firebase, authUser?.roles]);

  const loadMoreMessages = useCallback(() => {
    if (!firebase || messagesCursor == null || messagesLoading) return;
    setMessagesLoading(true);

    firebase.messages()
      .orderByChild('createdAt')
      .endBefore(messagesCursor)
      .limitToLast(PAGE_SIZE)
      .once('value')
      .then(snapshot => {
        const obj = snapshot.val();
        if (obj) {
          const newItems = Object.keys(obj)
            .map(k => ({ ...obj[k], uid: k }))
            .filter(msg =>
              !msg.organisation ||
              (authUser?.roles?.[msg.organisation] != null)
            )
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setMessages(prev => [...prev, ...newItems]);
          setMessagesHasMore(newItems.length >= PAGE_SIZE);
          if (newItems.length > 0) {
            setMessagesCursor(newItems[newItems.length - 1].createdAt);
          }
        } else {
          setMessagesHasMore(false);
        }
        setMessagesLoading(false);
      })
      .catch(() => setMessagesLoading(false));
  }, [firebase, messagesCursor, messagesLoading, authUser?.roles]);

  useEffect(() => {
    if (!firebase || !authUser?.roles) return;
    const orgs = Object.keys(authUser.roles).filter(k => authUser.roles[k] !== null);
    if (orgs.length === 0) {
      setLogsLoading(false);
      return;
    }

    const allLogs = {};
    const listeners = [];

    orgs.forEach(org => {
      const listener = firebase.onOrgActivityFeed(org, (logs) => {
        allLogs[org] = logs.map(log => ({ ...log, organization: org }));
        const combined = Object.values(allLogs).flat()
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setActivityLogs(combined.slice(0, PAGE_SIZE));
        setLogsHasMore(combined.length >= PAGE_SIZE);
        if (combined.length > 0) {
          setLogsCursor(combined[Math.min(combined.length, PAGE_SIZE) - 1].timestamp);
        }
        setLogsLoading(false);
      }, PAGE_SIZE);

      listeners.push({ org, listener });
    });

    logsListenersRef.current = listeners;

    return () => {
      logsListenersRef.current.forEach(({ org }) => {
        try { firebase.offOrgActivityFeed(org, PAGE_SIZE); } catch (e) { /* ignore */ }
      });
      logsListenersRef.current = [];
    };
  }, [firebase, authUser?.roles]);

  const loadMoreLogs = useCallback(() => {
    if (!firebase || !authUser?.roles || logsCursor == null || logsLoading) return;
    setLogsLoading(true);

    const orgs = Object.keys(authUser.roles).filter(k => authUser.roles[k] !== null);
    Promise.all(
      orgs.map(org =>
        firebase.doGetOrgActivityLogPaginated(org, PAGE_SIZE, logsCursor)
          .then(logs => logs.map(log => ({ ...log, organization: org })))
      )
    )
      .then(results => {
        const newItems = results
          .flat()
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setActivityLogs(prev => [...prev, ...newItems]);
        setLogsHasMore(newItems.length >= PAGE_SIZE);
        if (newItems.length > 0) {
          setLogsCursor(newItems[newItems.length - 1].timestamp);
        }
        setLogsLoading(false);
      })
      .catch(() => setLogsLoading(false));
  }, [firebase, authUser?.roles, logsCursor, logsLoading]);

  const handleViewChange = (newView) => {
    localStorage.setItem('notificationActiveTab', newView);
    setView(newView);
  };

  return (
    <div className="notifications-container">
      <div className="notifications-tabs">
        <button
          className={`notifications-tab ${view === 'messages' ? 'notifications-tab--active' : ''}`}
          onClick={() => handleViewChange('messages')}
        >
          {t('messages.messages', 'Messages')}
          {unreadMessages > 0 && <span className="notifications-tab-badge" />}
        </button>
        <button
          className={`notifications-tab ${view === 'logs' ? 'notifications-tab--active' : ''}`}
          onClick={() => handleViewChange('logs')}
        >
          {t('messages.activity_logs', 'Activity Logs')}
          {unreadLogs > 0 && <span className="notifications-tab-badge" />}
        </button>
        <button
          className="notifications-close-btn"
          onClick={onClose}
          aria-label="Close notifications"
          title="Close"
        >
          ✕
        </button>
      </div>

      {view === 'messages' && messagesLoading && messages.length === 0 && (
        <div className="notifications-loading">{t('messages.loading', 'Loading...')}</div>
      )}

      {view === 'messages' && !messagesLoading && messages.length === 0 && (
        <div className="notifications-empty">
          <p>{t('messages.no_messages')}</p>
        </div>
      )}

      {view === 'logs' && logsLoading && activityLogs.length === 0 && (
        <div className="notifications-loading">{t('messages.loading', 'Loading...')}</div>
      )}

      {view === 'logs' && !logsLoading && activityLogs.length === 0 && (
        <div className="notifications-empty">
          <p>{t('messages.no_activity_logs', 'No activity logs')}</p>
        </div>
      )}

      {view === 'messages' && messages.length > 0 && (
        <NotificationList
          messages={messages}
          activityLogs={activityLogs}
          view="messages"
          lastCheckTime={lastCheckTime}
          hasMore={messagesHasMore}
          loadingMore={messagesLoading}
          onLoadMore={loadMoreMessages}
        />
      )}

      {view === 'logs' && activityLogs.length > 0 && (
        <NotificationList
          messages={messages}
          activityLogs={activityLogs}
          view="logs"
          lastCheckTime={lastCheckTime}
          hasMore={logsHasMore}
          loadingMore={logsLoading}
          onLoadMore={loadMoreLogs}
        />
      )}

      <div className="notifications-settings" role="group" aria-label={t('messages.notification_settings', 'Notification settings')}>
        <div className="notifications-setting-row">
          <span className="notifications-setting-icon" aria-hidden="true">🔊</span>
          <span className="notifications-setting-label">
            {t('messages.sound', 'Sound')}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(soundEnabled)}
            aria-label={t('messages.notification_sound', 'Notification sound')}
            className={`notifications-switch${soundEnabled ? ' notifications-switch--on' : ''}`}
            onClick={() => onSoundEnabledChange && onSoundEnabledChange(!soundEnabled)}
          >
            <span className="notifications-switch__thumb" aria-hidden="true" />
          </button>
        </div>
        <div className="notifications-setting-row">
          <span className="notifications-setting-icon" aria-hidden="true">🔔</span>
          <span className="notifications-setting-label">
            {t('messages.toasts', 'Toasts')}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(toastsEnabled)}
            aria-label={t('messages.toast_notifications', 'Toast notifications')}
            className={`notifications-switch${toastsEnabled ? ' notifications-switch--on' : ''}`}
            onClick={() => onToastsEnabledChange && onToastsEnabledChange(!toastsEnabled)}
          >
            <span className="notifications-switch__thumb" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
